import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  DataTableV2,
  DataTableHeadV2,
  DataTableBodyV2,
  DataTableRowV2,
  DataTableHeadCellV2,
  DataTableCellV2,
} from '@americanexpress/dls-react';
import ESignConsent from '../Consent/ESignConsent';
import {
  setActiveELetter,
  setESignDisagreementBanner,
  setHasConsented,
} from '../../../duck';

const COLUMNS = [
  {
    key: 'dateReceived',
    intlId: 'table.col.date_received',
    label: 'Date Received',
  },
  { key: 'application', intlId: 'table.col.application', label: 'Application' },
  {
    key: 'statusOfApplication',
    intlId: 'table.col.status_of_application',
    label: 'Status of Application',
  },
  {
    key: 'actionRequired',
    intlId: 'table.col.action_required',
    label: 'Action Required',
  },
];

const formatApplicationDate = (dateStr) => {
  if (!dateStr) return dateStr;
  const normalized = dateStr.replace(
    /^(\d{4})-([A-Za-z]+)-(\d{2})/,
    '$3 $2 $1',
  );
  // "2026-May-06 17:20:14.014 UTC" → "06 May 2026 17:20:14.014 UTC"
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'numeric',
    day: '2-digit',
    year: 'numeric',
  });
};
const AppStatusTable = ({ statusData }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [selectedCommunicationId, setSelectedCommunicationId] = useState(null);
  const [selectedPCN, setSelectedPCN] = useState(null);

  // Set up hasConsented in the Redux store and a helper function to update it, which will be passed to the ESignConsent component. This allows us to track whether the user has consented across the app.
  const hasConsented = useSelector((state) =>
    state.getIn(['modules', 'unify-status-site', 'general', 'hasConsented']),
  );
  const applicantName = useSelector((state) =>
    state.getIn(['modules', 'unify-status-site', 'general', 'applicantName']),
  );
  const handleSetHasConsented = (consent) => dispatch(setHasConsented(consent));

  const handleConsentSuccess = (authorizationToken) => {
    const nextActiveELetter = {
      communicationId: selectedCommunicationId,
      pcn: selectedPCN,
      showConsentBanner: true,
    };

    if (authorizationToken) {
      nextActiveELetter.authorizationToken = authorizationToken;
    }

    dispatch(
      setActiveELetter(nextActiveELetter),
    );
    setIsConsentModalOpen(false);
    setSelectedCommunicationId(null);
    setSelectedPCN(null);
  };

  const handleConsentDenied = () => {
    setIsConsentModalOpen(false);
    dispatch(setESignDisagreementBanner(true));
    setSelectedCommunicationId(null);
    setSelectedPCN(null);
  };

  const handleOpenConsentModal = (communicationId, pcn) => {
    setSelectedCommunicationId(communicationId);
    setSelectedPCN(pcn);
    setIsConsentModalOpen(true);
  };

  const handleCloseConsentModal = () => {
    setIsConsentModalOpen(false);
    setSelectedCommunicationId(null);
    setSelectedPCN(null);
  };

  return (
    <>
      <p className="body-2 pad-2-b">
        {applicantName
          ? intl.formatMessage(
              { id: 'table.welcome', defaultMessage: 'Welcome {name}. Here is the status of the application(s) you submitted to us.' },
              { name: applicantName },
            )
          : intl.formatMessage({
              id: 'table.welcome.no_name',
              defaultMessage: 'Welcome. Here is the status of the application(s) you submitted to us.',
            })}
      </p>
      <DataTableV2>
        <DataTableHeadV2>
          <DataTableRowV2>
            {COLUMNS.map(({ key, intlId, label }) => (
              <DataTableHeadCellV2 key={key} scope="col">
                {intl.formatMessage({ id: intlId, defaultMessage: label })}
              </DataTableHeadCellV2>
            ))}
          </DataTableRowV2>
        </DataTableHeadV2>
        <DataTableBodyV2>
          {statusData?.map((row) => (
            <DataTableRowV2 key={row.id}>
              <DataTableCellV2>
                {formatApplicationDate(row.application_date)}
              </DataTableCellV2>
              <DataTableCellV2>{row.product_name}</DataTableCellV2>
              <DataTableCellV2>{row.status}</DataTableCellV2>
              <DataTableCellV2>
                {row.status_description && (
                  <span>{row.status_description}</span>
                ) }
                {' '}
                {row.next_step && (
                  <button
                    type="button"
                    onClick={() =>
                      handleOpenConsentModal(row.communication_id, row.pcn)
                    }
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'blue',
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                  >
                    {row.next_step}
                  </button>
                )}
              </DataTableCellV2>
            </DataTableRowV2>
          ))}
        </DataTableBodyV2>
      </DataTableV2>
      <p className="body-2 pad-2-t mar-0">
        The information above is the latest we have regarding your application(s). If you don&apos;t
        see the Card(s) or loan you applied for listed here, or if you have any questions, please
        call us at 1-866-314-0237 (Mon-Fri 6am-8pm MST).
      </p>
      {isConsentModalOpen && (
        <ESignConsent
          isOpen={isConsentModalOpen}
          onClose={handleCloseConsentModal}
          setHasConsented={handleSetHasConsented}
          onConsentSuccess={handleConsentSuccess}
          onConsentDenied={handleConsentDenied}
          hasConsented={hasConsented}
          pcn={selectedPCN}
        />
      )}
    </>
  );
};

AppStatusTable.propTypes = {
  statusData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      dateReceived: PropTypes.string.isRequired,
      application: PropTypes.string.isRequired,
      statusOfApplication: PropTypes.string.isRequired,
      actionRequired: PropTypes.string.isRequired,
    }),
  ),
};

AppStatusTable.defaultProps = {
  statusData: [],
};

export default AppStatusTable;
